// src/features/plan/marketsService.js
//
// PLAN-S1 — Pure service functions for the Plan · Markets view.
// No React; no data-client imports.  getDisplayedSqft is a pure utility.
// All inputs and outputs are plain objects so these functions can be
// tested in isolation via Vitest without any DOM setup.

import { getAnalyticalSqft as getDisplayedSqft } from "../../utils/locationSqftDerived.js";
import { ACTION_DATE_TYPES } from "../leases/deriveLeaseActions.js";

const INACTIVE_STAGES = new Set(["draft", "closed", "vacated"]);
const OPERATING_STAGES = new Set(["operational", "dormant"]);
const ESTABLISHING_STAGES = new Set(["build"]);

// MARKETPLAN-OFFICE-MEMBERSHIP-SINGLE-SOURCE: two distinct, single-source
// selectors for "which offices belong to a market" questions. Never conflate
// them — see docs on each for the rationale.
//
// - isMarketMember: browsable membership, including Draft scaffolding.
// - isCountedMarketOffice: effective office headlines, excluding Draft but
//   preserving Vacated membership/count semantics.
// - isOperationallyActive: "is this office live/occupying capacity right
//   now" — used for seats/utilization/headroom/coverage math. Excludes
//   'closed' AND 'vacated'.
// MARKET-PLAN-STAGE-LEASE-COLUMNS invariant: both helpers key exclusively on
// loc.stage. Neither reads loc.leaseStatus. The Stage and Lease columns in
// OfficesTable are independent — leaseStatus never influences membership or
// capacity math.
export function isMarketMember(loc) {
  // Invariant: keyed on loc.stage only; loc.leaseStatus has no effect here.
  return (loc?.stage ?? "") !== "closed";
}

// Browsable Draft scaffolding is not an effective office count.
// Preserve the existing Vacated membership/count semantics.
export function isCountedMarketOffice(loc) {
  return isMarketMember(loc) && loc?.stage !== "draft";
}

export function isOperationallyActive(loc) {
  // Invariant: keyed on loc.stage only; loc.leaseStatus has no effect here.
  return !INACTIVE_STAGES.has(loc?.stage ?? "");
}

/**
 * Derive roll-up stats for a market from the locations that belong to it.
 *
 * @param {object}   market             Market row (camelCase, from marketsClient)
 * @param {object[]} locations          Array of location rows for this market
 *                                      (must have sqft, installedCapacity, latestHeadcount?, stage)
 * @param {Record<string, object[]>} [leasesByLocation={}]
 *                                      Optional: locationId → leases[] map.
 *                                      When provided, annualCostUsd is computed
 *                                      from the sum of active leases' totalCost.
 * @returns {{
 *   locationCount:        number,
 *   activeLocationCount:  number,
 *   headcount:            number | null,
 *   seats:                number,
 *   sqft:                 number,
 *   annualCostUsd:        number | null,   null when no lease cost data available
 *   consolidateSqft:      number | null,   sqft of consolidate-dispositioned locs (negative = reduction)
 *   noActiveFootprint:    boolean,
 * }}
 */
export function deriveMarketStats(market, locations, leasesByLocation = {}) {
  if (!Array.isArray(locations) || locations.length === 0) {
    return {
      locationCount:       0,
      activeLocationCount: 0,
      headcount:           null,
      seats:               0,
      sqft:                0,
      annualCostUsd:       null,
      consolidateSqft:     null,
      noActiveFootprint:   true,
    };
  }

  let activeCount    = 0;
  let totalSqft      = 0;
  let totalSeats     = 0;
  let totalHeadcount = null;
  let hasHeadcount   = false;
  let annualCost     = null;
  let hasLeaseCost   = false;
  let consolidateSqft = null;

  for (const loc of locations) {
    if (loc.stage === "draft") continue;
    const stage = loc.stage ?? "";
    const isActive = !INACTIVE_STAGES.has(stage);
    if (isActive) activeCount += 1;

    // Preserve Plan's pre-existing canonical/stored area read contract.
    const sqft = getDisplayedSqft(loc, null) ?? 0;
    totalSqft  += sqft;
    totalSeats += typeof loc.installedCapacity === "number" ? loc.installedCapacity : 0;

    // latestHeadcount is an object from v_headcount_current join: { totalCount, ... }
    const hc = loc.latestHeadcount?.totalCount ?? loc.latestHeadcount?.total_count;
    if (typeof hc === "number" && hc > 0) {
      hasHeadcount   = true;
      totalHeadcount = (totalHeadcount ?? 0) + hc;
    }

    // Annual cost — sum active leases' totalCost (already in location currency).
    // The consumer is responsible for FX normalisation if cross-currency rollup is
    // needed; here we sum naively and treat the result as "USD-equivalent".
    const locLeases = leasesByLocation[loc.id] ?? [];
    for (const lse of locLeases) {
      if (lse.status === "active" && lse.totalCost != null) {
        const cost = Number(lse.totalCost);
        if (!isNaN(cost)) {
          hasLeaseCost = true;
          annualCost   = (annualCost ?? 0) + cost;
        }
      }
    }

    // Consolidate net-change: sum sqft for exit+consolidate locations.
    // After DISPOSITION-CONSOLIDATE, these rows have disposition="exit" and
    // exit_reason="consolidate". Negative because consolidation reduces footprint.
    if (loc.disposition === "exit" && loc.exit_reason === "consolidate") {
      consolidateSqft = (consolidateSqft ?? 0) - sqft;
    }
  }

  return {
    locationCount:       locations.filter((loc) => loc.stage !== "draft").length,
    activeLocationCount: activeCount,
    headcount:           hasHeadcount ? totalHeadcount : null,
    seats:               totalSeats,
    sqft:                totalSqft,
    annualCostUsd:       hasLeaseCost ? annualCost : null,
    consolidateSqft:     consolidateSqft,
    noActiveFootprint:   activeCount === 0,
  };
}

/**
 * Group an array of locations by their marketId.
 * Locations with no marketId are placed under the key `__unassigned__`.
 *
 * @param {object[]} locations
 * @returns {Record<string, object[]>}
 */
export function groupLocationsByMarket(locations) {
  const groups = {};
  for (const loc of locations) {
    const key = loc.marketId ?? "__unassigned__";
    if (!groups[key]) groups[key] = [];
    groups[key].push(loc);
  }
  return groups;
}

/**
 * Sort markets for display: alphabetically within region.
 *
 * @param {object[]} markets
 * @returns {object[]}
 */
export function sortMarketsForDisplay(markets) {
  return [...markets].sort((a, b) => {
    if (a.region !== b.region) return a.region.localeCompare(b.region);
    return a.name.localeCompare(b.name);
  });
}

/**
 * Derive horizon buckets from locations with disposition data.
 * Groups locations by disposition type and then by target-date quarter.
 *
 * Each bucket: { disposition, quarter, year, locations: object[], pastDue: boolean }
 * Locations with no dispositionTarget fall into a { quarter: null } bucket per
 * disposition type.
 *
 * @param {object[]} locations  All locations (with disposition, dispositionTarget)
 * @returns {object[]}          Sorted bucket list
 */
export function deriveHorizonBuckets(locations) {
  const map = {};  // key: `${disposition}:${year}:${quarter}` or `${disposition}:null`

  const today = new Date();
  const currentYear    = today.getFullYear();
  const currentQuarter = Math.ceil((today.getMonth() + 1) / 3);

  for (const loc of locations) {
    if (!loc.disposition) continue;

    let year = null, quarter = null;
    if (loc.dispositionTarget) {
      const d = new Date(loc.dispositionTarget);
      if (!isNaN(d.getTime())) {
        year    = d.getFullYear();
        quarter = Math.ceil((d.getMonth() + 1) / 3);
      }
    }

    const key = `${loc.disposition}:${year}:${quarter}`;
    if (!map[key]) {
      const pastDue = year !== null && (
        year < currentYear || (year === currentYear && quarter < currentQuarter)
      );
      map[key] = {
        disposition: loc.disposition,
        year,
        quarter,
        pastDue,
        locations: [],
      };
    }
    map[key].locations.push(loc);
  }

  return Object.values(map).sort((a, b) => {
    // Disposition order — 6-value set after DISPOSITION-CONSOLIDATE
    const ORDER = ["stay","expand","rightsize","exit","prospect","undecided"];
    const ai = ORDER.indexOf(a.disposition);
    const bi = ORDER.indexOf(b.disposition);
    if (ai !== bi) return ai - bi;
    // null dates last
    if (a.year === null && b.year === null) return 0;
    if (a.year === null) return 1;
    if (b.year === null) return -1;
    if (a.year !== b.year) return a.year - b.year;
    return (a.quarter ?? 0) - (b.quarter ?? 0);
  });
}

/**
 * Format a quarter label, e.g. { year: 2026, quarter: 2 } → "Q2 2026"
 * Returns "No target" when year/quarter are null.
 *
 * @param {number|null} year
 * @param {number|null} quarter
 * @returns {string}
 */
export function formatQuarterLabel(year, quarter) {
  if (year === null || quarter === null) return "No target";
  return `Q${quarter} ${year}`;
}

// ─── What-If scenario math (WHATIF-S1) ───────────────────────────────────────
// All formulas are documented inline; the peak_ratio assumption is surfaced in
// the UI chain text so users can see and reason about it.

/**
 * Sum installed capacity and current headcount for a market's non-inactive
 * locations. Used as the data layer for What-If scenario math.
 *
 * Inactive stages (closed, vacated) are excluded so decommissioned space does
 * not inflate the capacity ceiling.
 *
 * @param {object[]} locations  Location rows (from plan or merged with /api/locations)
 * @returns {{ capacity: number, currentHC: number }}
 */
export function deriveMarketCapacityStats(locations) {
  let capacity  = 0;
  let currentHC = 0;
  for (const loc of (locations ?? [])) {
    if (INACTIVE_STAGES.has(loc.stage ?? "")) continue;
    if (typeof loc.installedCapacity === "number") capacity += loc.installedCapacity;
    const hc = loc.latestHeadcount?.totalCount ?? loc.latestHeadcount?.total_count;
    if (typeof hc === "number" && hc > 0) currentHC += hc;
  }
  return { capacity, currentHC };
}

/**
 * Add N whole months to a date, in UTC, returning a new Date. Used to derive
 * the What-If scenario horizon date (now + levers.horizonMonths) for the
 * horizon-aware capacity ceiling.
 *
 * @param {Date} date
 * @param {number} months
 * @returns {Date}
 */
function addMonthsUtc(date, months) {
  const d = new Date(date.getTime());
  d.setUTCMonth(d.getUTCMonth() + months);
  return d;
}

/**
 * WHATIF-CAPACITY-PHASING-S1 — horizon-effective seat contribution for a
 * single office. Mirrors the missing-date philosophy already established by
 * computeOfficeLifecycleSegments (below): never fabricate a transition date.
 *
 * - Closed/vacated (INACTIVE_STAGES): always 0.
 * - In-build stages (IN_BUILD_STAGES — build/negotiating/prospecting):
 *   contributes `plannedSeats` once `onlineDate` is set AND `onlineDate` has
 *   passed the horizon; otherwise 0 (including when `onlineDate` is
 *   missing — still building, no fabricated go-live date).
 * - Any other (operational) stage: contributes `installedCapacity`, unless
 *   the office is exit-dispositioned with a `closeDate` that has passed the
 *   horizon (then 0). A missing `closeDate` on an exiting office never drops
 *   it — no fabricated close date.
 *
 * @param {object} office  Location row (camelCase): stage, disposition,
 *                          onlineDate, closeDate, installedCapacity, plannedSeats.
 * @param {Date|string} horizon  The as-of date to evaluate capacity at.
 * @returns {number}
 */
export function computeOfficeHorizonCapacity(office, horizon) {
  const stage = office?.stage ?? "";
  if (INACTIVE_STAGES.has(stage)) return 0;

  const h = toUtcDate(horizon);

  if (IN_BUILD_STAGES.has(stage)) {
    // FORWARD-CAPACITY-CONSUMERS-READ-SCHEDULE: check the schedule *before*
    // the legacy onlineDate/plannedSeats gate — a scheduled build must phase
    // its seats in at the schedule row's own effective date even when
    // onlineDate is unset or falls after the horizon. Only fall back to the
    // legacy gate when this office has no schedule data at all (or none of
    // its rows are effective yet at this horizon).
    const scheduleSeats = scheduleCapacityAtHorizon(office?.futureScheduleRows, h);
    if (scheduleSeats !== null) return scheduleSeats;
    const onlineDate = parseTimelineDate(office?.onlineDate);
    if (onlineDate == null || onlineDate > h) return 0;
    return typeof office?.plannedSeats === "number" ? office.plannedSeats : 0;
  }

  if (office?.disposition === EXIT_DISPOSITION) {
    const closeDate = parseTimelineDate(office?.closeDate);
    if (closeDate != null && closeDate <= h) return 0;
  }

  // CAPACITY-SCHEDULE-S1: prefer schedule-derived seats for the horizon date.
  const scheduleSeats = scheduleCapacityAtHorizon(office?.futureScheduleRows, h);
  if (scheduleSeats !== null) return scheduleSeats;
  return typeof office?.installedCapacity === "number" ? office.installedCapacity : 0;
}

/**
 * CAPACITY-SCHEDULE-S1 internal helper: find the most-recent schedule row
 * whose effectiveDate is at or before `h`.  Returns that row (with
 * installedSeats/maxDensity), or null when no row qualifies (so the caller
 * can fall back to flat fields).
 *
 * `futureScheduleRows` comes from the plan API response — each element has
 * { effectiveDate (YYYY-MM-DD string), installedSeats (number), maxDensity
 * (number|null) }.  The array is sorted ascending by effectiveDate so we
 * iterate and keep the last match.
 */
function effectiveScheduleRowAtHorizon(futureScheduleRows, h) {
  if (!Array.isArray(futureScheduleRows) || futureScheduleRows.length === 0) return null;
  let best = null;
  for (const r of futureScheduleRows) {
    const d = parseTimelineDate(r.effectiveDate);
    if (d != null && d <= h) best = r;
  }
  return best;
}

function scheduleCapacityAtHorizon(futureScheduleRows, h) {
  const row = effectiveScheduleRowAtHorizon(futureScheduleRows, h);
  return row !== null ? row.installedSeats : null;
}

/**
 * MARKET-PLAN-HEADROOM-S1 — horizon-effective Headroom (max density) for a
 * single office. Headroom = maxCapacity − installedCapacity (never negative),
 * gated by exactly the same lifecycle rules as computeOfficeHorizonCapacity:
 * it only counts for currently-live offices, phases in once a build office's
 * onlineDate is reached, and drops to 0 once an exiting office's closeDate
 * passes. Returns null (not 0) whenever maxCapacity is unset — that means
 * "no data on file", never a fabricated number.
 *
 * @param {object} office  Location row (camelCase): stage, disposition,
 *                          onlineDate, closeDate, installedCapacity,
 *                          plannedSeats, maxCapacity.
 * @param {Date|string} horizon  The as-of date to evaluate headroom at.
 * @returns {number|null}
 */
export function computeOfficeHorizonHeadroom(office, horizon) {
  const stage = office?.stage ?? "";
  if (INACTIVE_STAGES.has(stage)) return 0;

  const h = toUtcDate(horizon);

  if (IN_BUILD_STAGES.has(stage)) {
    // FORWARD-CAPACITY-CONSUMERS-READ-SCHEDULE: prefer the schedule's
    // horizon-effective row for both installed seats and max density —
    // check it before ever consulting the legacy onlineDate/plannedSeats
    // gate and today's flat maxCapacity (mirrors computeOfficeHorizonCapacity).
    const scheduleRow = effectiveScheduleRowAtHorizon(office?.futureScheduleRows, h);
    if (scheduleRow !== null) {
      const maxCapacity = typeof scheduleRow.maxDensity === "number" ? scheduleRow.maxDensity : office?.maxCapacity;
      if (typeof maxCapacity !== "number") return null;
      return Math.max(0, maxCapacity - scheduleRow.installedSeats);
    }
    const maxCapacity = office?.maxCapacity;
    if (typeof maxCapacity !== "number") return null;
    const onlineDate = parseTimelineDate(office?.onlineDate);
    if (onlineDate == null || onlineDate > h) return 0;
    const installed = typeof office?.plannedSeats === "number" ? office.plannedSeats : 0;
    return Math.max(0, maxCapacity - installed);
  }

  const maxCapacity = office?.maxCapacity;
  if (typeof maxCapacity !== "number") return null;

  if (office?.disposition === EXIT_DISPOSITION) {
    const closeDate = parseTimelineDate(office?.closeDate);
    if (closeDate != null && closeDate <= h) return 0;
  }

  // FORWARD-CAPACITY-CONSUMERS-READ-SCHEDULE: prefer schedule-derived installed
  // seats and max density for the horizon date, mirroring computeOfficeHorizonCapacity.
  const scheduleRow = effectiveScheduleRowAtHorizon(office?.futureScheduleRows, h);
  if (scheduleRow !== null) {
    const maxCap = typeof scheduleRow.maxDensity === "number" ? scheduleRow.maxDensity : maxCapacity;
    return Math.max(0, maxCap - scheduleRow.installedSeats);
  }

  const installed = typeof office?.installedCapacity === "number" ? office.installedCapacity : 0;
  return Math.max(0, maxCapacity - installed);
}

/**
 * Market-level Headroom rollup at a given horizon: sums
 * computeOfficeHorizonHeadroom() across offices that have maxCapacity on
 * file. Returns null (not 0) when no office in the market has maxCapacity
 * set — the UI renders "—" rather than a fabricated 0.
 *
 * @param {object[]} locations  Location rows for a market.
 * @param {Date|string} horizon  The as-of date to evaluate headroom at.
 * @returns {number|null}
 */
export function deriveMarketHeadroomAtHorizon(locations, horizon) {
  let total = null;
  for (const loc of (locations ?? [])) {
    const h = computeOfficeHorizonHeadroom(loc, horizon);
    if (h !== null) total = (total ?? 0) + h;
  }
  return total;
}

/**
 * Horizon-aware variant of deriveMarketCapacityStats: sums each office's
 * computeOfficeHorizonCapacity() contribution as of a given horizon date,
 * instead of the static "does this office currently have installed
 * capacity" sum. Used by computeWhatIfMarketScenario so the What-If capacity
 * ceiling responds to the selected horizon the same way demand already does.
 *
 * Calling this with `horizon = new Date()` reproduces deriveMarketCapacityStats'
 * capacity total exactly for today's baseline (build offices whose onlineDate
 * hasn't passed still contribute 0; no office has closeDate <= today unless
 * it should already read as closed).
 *
 * @param {object[]} locations  Location rows for a market.
 * @param {Date|string} horizon  The as-of date to evaluate capacity at.
 * @returns {{ capacity: number }}
 */
export function deriveMarketCapacityStatsAtHorizon(locations, horizon) {
  let capacity = 0;
  for (const loc of (locations ?? [])) {
    capacity += computeOfficeHorizonCapacity(loc, horizon);
  }
  return { capacity };
}

/**
 * Peak attendance ratio as a clamped linear function of in-office days/month.
 *
 * Formula: peak_ratio = clamp(0.25 + rto_days × 0.036, 0.25, 0.97)
 *
 * Intuition: 0 days/mo → 25% of staff in on any peak day (fully-remote baseline);
 * 20 days/mo → 0.25 + 20×0.036 = 0.97 (near-full occupancy).
 * Calibrating this against actual badge-attendance data is an S3 refinement.
 *
 * @param {number} rtoDays  In-office days per month (0–20)
 * @returns {number}        Peak ratio in [0.25, 0.97]
 */
export function peakRatio(rtoDays) {
  return Math.max(0.25, Math.min(0.97, 0.25 + rtoDays * 0.036));
}

/**
 * Calculate scenario seats needed and surplus/deficit for one market.
 *
 * Math (all formulas shown; do not simplify without updating the chain text in WhatIfPane):
 *   projected_HC = current_HC × (1 + growth/100) ^ years
 *   seats_needed = projected_HC × peak_ratio(rto)
 *   squeeze      = capacity − seats_needed  (positive = surplus, negative = deficit)
 *   util         = seats_needed / capacity
 *
 * WHATIF-S2 open-role alloc (optional 4th param):
 *   openRoleAlloc = { expected, low, high } from allocateRolesToMarkets().
 *   When absent (or hire toggle is OFF) it defaults to zeroes so S1 callers
 *   see no behavioural change.
 *
 * @param {number} currentHC   Current headcount for this market
 * @param {number} capacity    Total installed capacity for this market
 * @param {{ rto: number, grow: number, hz: number }} scenario
 * @param {{ expected: number, low: number, high: number }} [openRoleAlloc]
 * @returns {{
 *   projHC: number,
 *   seatsNeeded: number, squeeze: number, util: number, peak: number,
 *   projHC_expected: number, projHC_low: number, projHC_high: number,
 * }}
 */
export function calcScenario(currentHC, capacity, scenario, openRoleAlloc) {
  const { rto, grow, hz } = scenario;
  const peak       = peakRatio(rto);
  const grownHC    = currentHC * Math.pow(1 + grow / 100, hz);
  const projHC     = Math.round(grownHC);

  // Open-role fractional allocation (WHATIF-S2).
  // Defaults to 0 when not supplied so S1 callers are unaffected.
  const alloc = openRoleAlloc ?? { expected: 0, low: 0, high: 0 };

  const projHC_expected = Math.round((grownHC + alloc.expected) * peak);
  const projHC_low      = Math.round((grownHC + alloc.low)      * peak);
  const projHC_high     = Math.round((grownHC + alloc.high)     * peak);

  // seatsNeeded = expected variant (= pure S1 value when alloc=0)
  const seatsNeeded = projHC_expected;
  const squeeze     = capacity - seatsNeeded;

  // When capacity is 0 but seats are needed, the market has recorded headcount
  // but no capacity data — treat as over-capacity (Infinity) rather than
  // returning 0 which would show a false-green RAG state.
  const util = capacity > 0
    ? seatsNeeded / capacity
    : (seatsNeeded > 0 ? Infinity : 0);

  return { projHC, seatsNeeded, squeeze, util, peak, projHC_expected, projHC_low, projHC_high };
}

// ─── PLAN-WHATIF-S1 — Market Plan "Plan | What-If" scenario lens ─────────────
//
// This is the canonical scenario-math implementation for the read-only
// What-If lens on the Market Plan page (see WhatIfPane.jsx). It is
// deliberately separate from calcScenario()/peakRatio() above (Pulse's
// SpaceHeadroomPane WHATIF-S2 growth/RTO/open-roles model): that pane answers
// "how many seats will Greenhouse hiring need", this one answers "how would
// supply-side levers (sharing, hoteling, conf-room give-back) and demand
// growth reshape each market's assignment % against its own baseline,
// entirely without touching plan data." Pulse's pane links out to this lens
// (see the `space-headroom-whatif-link` anchor) rather than re-implementing
// this math, so the two views never diverge silently.

/** Horizon options offered by the What-If horizon selector (months out). */
export const WHATIF_HORIZON_OPTIONS = [
  { label: "Now",   months: 0 },
  { label: "6 mo",  months: 6 },
  { label: "12 mo", months: 12 },
  { label: "18 mo", months: 18 },
  { label: "24 mo", months: 24 },
];

/** Fixed guardrail thresholds shown as read-only chips (not user-editable in S1). */
export const WHATIF_GUARDRAILS = {
  densityFloorSfPerSeat: 150,
  targetBufferPct:       10,
};

/**
 * Default lever state, matching the design comp's initial values.
 *
 * WHATIF-NETGROWTH-PERMARKET-S1: net growth is now two pieces — a single
 * `netGrowthDefault` rate that applies to every market NOT individually
 * pinned, plus `netGrowthByMarket` (market id → pinned rate) for markets a
 * planner has explicitly overridden. The collapsed lever's displayed value
 * is always the headcount-weighted blend of every active market's effective
 * rate (see computeBlendedNetGrowthRate) — never a directly-set number.
 */
export const WHATIF_DEFAULT_LEVERS = {
  horizonMonths:      12,
  netGrowthDefault:   4,
  netGrowthByMarket:  {},
  rtoDaysPerMonth:    10,
  sharingRatio:       1.0,
  hotelingHoldPct:    5,
  confGivebackPct:    0,
  teamBlockWastePct:  0,
};

/**
 * PLAN-WHATIF-REDESIGN-S1 — "Reset to today" target lever set.
 *
 * Distinct from WHATIF_DEFAULT_LEVERS (the suggested-scenario starting point,
 * which already assumes +4% growth): this is the neutral lever set that makes
 * every Scenario figure exactly equal its Today figure (all deltas 0). Net
 * growth is 0 (demand parity regardless of horizon), RTO is pinned to
 * WHATIF_DEFAULT_LEVERS.rtoDaysPerMonth (the baseline the peak-attendance
 * rtoFactor is normalized against in computeWhatIfMarketScenario, so
 * rtoFactor === 1), and every supply lever is at its "no-op" value
 * (sharingRatio 1:1, hold/give-back/waste all 0%) so usableSeatsScenario
 * === installedCapacity.
 */
export const WHATIF_NEUTRAL_LEVERS = {
  horizonMonths:      WHATIF_DEFAULT_LEVERS.horizonMonths,
  netGrowthDefault:   0,
  netGrowthByMarket:  {},
  rtoDaysPerMonth:    WHATIF_DEFAULT_LEVERS.rtoDaysPerMonth,
  sharingRatio:       1.0,
  hotelingHoldPct:    0,
  confGivebackPct:    0,
  teamBlockWastePct:  0,
};

/**
 * Shared metadata for the 5 portfolio KPI tiles rendered above the Plan/
 * What-If toolbar. Plan mode renders one Today number per tile (except this
 * list itself has no "today/scenario" notion); What-If mode splits every
 * tile except "markets" into Today/Scenario blocks with a delta.
 */
export const KPI_TILE_DEFS = [
  { key: "markets",           label: "Markets" },
  { key: "annual-cost",       label: "Annual cost" },
  { key: "assignment-ratio",  label: "Assignment ratio" },
  { key: "over-capacity",     label: "Markets over capacity" },
  { key: "portfolio-balance", label: "Portfolio balance" },
];

/**
 * Usable seats for a market under a given lever set.
 *
 * Formula: usable_seats = installed × sharing_ratio
 *                        − installed × hoteling_hold%
 *                        + installed × conf_giveback%
 *                        − installed × team_block_waste%
 *
 * All supply levers are GLOBAL percentages applied relative to each office's
 * own installed capacity, so a market that is already denser (higher
 * installed-vs-headcount ratio) stays proportionally denser after the same
 * global lever move — no per-office baseline table is needed.
 *
 * @param {number} installedCapacity
 * @param {{ sharingRatio: number, hotelingHoldPct: number, confGivebackPct: number, teamBlockWastePct: number }} levers
 * @returns {number}
 */
export function computeUsableSeats(installedCapacity, levers) {
  const cap = installedCapacity ?? 0;
  const { sharingRatio, hotelingHoldPct, confGivebackPct, teamBlockWastePct } = levers;
  return cap * sharingRatio
       - cap * (hotelingHoldPct / 100)
       + cap * (confGivebackPct / 100)
       - cap * (teamBlockWastePct / 100);
}

/**
 * Projected headcount (demand) for a market at the chosen horizon.
 *
 * Formula: demand = headcount × (1 + net_growth% / 100) ^ (horizon_months / 12)
 *
 * Growth compounds annually and is pro-rated by the fraction of a year the
 * horizon represents (matches calcScenario's ^years convention above).
 *
 * @param {number} headcount
 * @param {number} netGrowthPct
 * @param {number} horizonMonths
 * @returns {number}
 */
export function computeWhatIfDemand(headcount, netGrowthPct, horizonMonths) {
  const hc = headcount ?? 0;
  return hc * Math.pow(1 + netGrowthPct / 100, horizonMonths / 12);
}

// ─── WHATIF-NETGROWTH-PERMARKET-S1 — per-market net growth overrides ────────

/**
 * A market counts as ACTIVE for What-If net-growth weighting/editing when it
 * is not exited/closed. Mirrors MarketPlanPage.jsx's isMarketExited() check
 * (status === "Exited" OR active === false) so the two stay in lockstep.
 * @param {object} market
 * @returns {boolean}
 */
export function isWhatIfActiveMarket(market) {
  return !(market?.status === "Exited" || market?.active === false);
}

// ─── MARKET-PLAN-PURGE-TESTS-AND-RECONCILE-COUNT-S1 ──────────────────────────
// Canonical "active market set" for portfolio KPI purposes, shared by Plan's
// KpiStrip, What-If's WhatIfKpiStrip, and Timeline's heatmap so the Markets/
// Peak util/Over-capacity/Portfolio balance tiles read the same numbers for
// the same underlying data no matter which view is showing them.
//
// Decision (documented here since it's not obvious from the code alone):
//   - Exited-status / inactive markets (isWhatIfActiveMarket === false) are
//     EXCLUDED — a market a team has exited is no longer part of the live
//     portfolio's capacity/demand story.
//   - 0-office markets are INCLUDED — a market can legitimately be active
//     (staffing up, between leases) with no offices yet; excluding it would
//     make "Markets" undercount real, intentional portfolio entries. This
//     mirrors isWhatIfActiveMarket/isMarketExited, neither of which has ever
//     looked at office/location counts.
//   - This is independent of any view-local UI filtering (search, strategy/
//     region/status/flags/hub filters, or Timeline's own "has a live office"
//     rendering filter for its heatmap grid) — those remain view-specific and
//     must not feed the shared KPI numbers.
export const isCanonicalActiveMarket = isWhatIfActiveMarket;

/**
 * Filters a market list down to the canonical active-market set used for
 * portfolio KPI computation (see isCanonicalActiveMarket above).
 * @param {object[]} markets
 * @returns {object[]}
 */
export function selectActiveMarketsForKpis(markets) {
  return (markets ?? []).filter(isCanonicalActiveMarket);
}

/**
 * Return the canonical active-market count used by portfolio KPI surfaces.
 * Keep the selector and count together so callers cannot accidentally count
 * view-filtered rows or reimplement the active/non-exited predicate.
 * @param {object[]} markets
 * @returns {number}
 */
export function getActiveMarketKpiCount(markets) {
  return selectActiveMarketsForKpis(markets).length;
}

/**
 * Count distinct markets represented by a set of office rows.
 * Prefer the canonical market ID fields, then fall back to the display name.
 * Blank and missing market keys are intentionally ignored.
 * @param {object[]} locations
 * @returns {number}
 */
export function getDistinctMarketCount(locations) {
  const marketKeys = new Set();
  for (const location of locations ?? []) {
    const canonicalId = location?.marketId ?? location?.market_id;
    const fallbackName = location?.market;
    const key = canonicalId != null && String(canonicalId).trim() !== ""
      ? canonicalId
      : fallbackName;
    if (key != null && String(key).trim() !== "") marketKeys.add(String(key));
  }
  return marketKeys.size;
}

/**
 * Derive a market's portfolio presence label from its non-deleted offices.
 *
 * Office stages intentionally have precedence over the market lifecycle
 * fields: a market with any operating office is Operating even if it also has
 * build or closed offices, and a market with only build offices is Establishing.
 * Markets with no usable stage evidence fall back to their lifecycle state.
 *
 * @param {object} market
 * @returns {"Operating"|"Establishing"|"Exited"|"Prospecting"}
 */
export function classifyMarketPresence(market) {
  const locations = Array.isArray(market?.locations) ? market.locations : [];
  const stages = locations
    .filter((location) => location?.deletedAt == null && location?.deleted_at == null)
    .map((location) => String(location?.stage ?? "").trim().toLowerCase());

  if (stages.some((stage) => OPERATING_STAGES.has(stage))) return "Operating";
  if (stages.some((stage) => ESTABLISHING_STAGES.has(stage))) return "Establishing";
  if (stages.some((stage) => stage !== "draft" && INACTIVE_STAGES.has(stage))) return "Exited";
  if (market?.status === "Exited" || market?.active === false) return "Exited";
  return "Prospecting";
}

function getActiveMarketPresenceCount(markets, presence) {
  return selectActiveMarketsForKpis(markets)
    .filter((market) => classifyMarketPresence(market) === presence)
    .length;
}

/**
 * Return the number of canonically active markets with operating offices.
 * @param {object[]} markets
 * @returns {number}
 */
export function getOperatingMarketKpiCount(markets) {
  return getActiveMarketPresenceCount(markets, "Operating");
}

/**
 * Return the number of canonically active markets with build offices.
 * @param {object[]} markets
 * @returns {number}
 */
export function getEstablishingMarketKpiCount(markets) {
  return getActiveMarketPresenceCount(markets, "Establishing");
}

/**
 * The effective net-growth rate for a single market: its own pinned
 * override if one is set in `levers.netGrowthByMarket`, otherwise the
 * shared `levers.netGrowthDefault`.
 * @param {{ id: string }} market
 * @param {{ netGrowthDefault: number, netGrowthByMarket?: Record<string, number> }} levers
 * @returns {number}
 */
export function getEffectiveNetGrowthRate(market, levers) {
  const overrides = levers?.netGrowthByMarket ?? {};
  const pinned = market?.id != null ? overrides[market.id] : undefined;
  return pinned !== undefined && pinned !== null ? pinned : (levers?.netGrowthDefault ?? 0);
}

/**
 * Headcount-weighted blend of every ACTIVE market's effective net-growth
 * rate: Σ(market HC × its rate) ÷ Σ HC. This is what the collapsed Net
 * growth lever always displays — a read-through of the per-market mix,
 * never a directly-editable number. Exited/closed markets are excluded
 * from both the numerator and the headcount denominator. Falls back to
 * `levers.netGrowthDefault` when there is no active headcount to weight by
 * (avoids a divide-by-zero and keeps the collapsed value meaningful).
 * @param {object[]} markets
 * @param {{ netGrowthDefault: number, netGrowthByMarket?: Record<string, number> }} levers
 * @returns {number}
 */
export function computeBlendedNetGrowthRate(markets, levers) {
  const activeMarkets = (markets ?? []).filter(isWhatIfActiveMarket);
  let weightedSum = 0;
  let totalHc = 0;
  for (const market of activeMarkets) {
    const hc = market.headcount ?? market.totalHeadcount ?? 0;
    weightedSum += hc * getEffectiveNetGrowthRate(market, levers);
    totalHc += hc;
  }
  return totalHc > 0 ? weightedSum / totalHc : (levers?.netGrowthDefault ?? 0);
}

/**
 * Full deterministic What-If scenario for a single market.
 *
 * - "Today" figures use the market's actual current headcount and installed
 *   capacity — no levers applied — so the Today bar always reflects reality.
 * - "Scenario" figures apply the demand growth + supply levers above, PLUS a
 *   peak-attendance adjustment driven by the RTO (days-in-office/mo) lever:
 *
 *     rtoFactor      = peakRatio(rtoDaysPerMonth) / peakRatio(BASELINE_RTO)
 *     demandScenario = computeWhatIfDemand(headcount, netGrowthPct, horizonMonths) × rtoFactor
 *
 *   BASELINE_RTO is WHATIF_DEFAULT_LEVERS.rtoDaysPerMonth, so rtoFactor is
 *   exactly 1 whenever the RTO lever is left at its default — this is what
 *   keeps "neutral levers ⇒ scenario === today" true while still making
 *   peak-attendance pressure a first-class driver of assignment %, Δ seats,
 *   over-capacity, and the guardrail checks (not just a separate "crowded"
 *   chip) whenever a planner actually moves the RTO lever away from default.
 * - Δ seats is the change in headroom (surplus), i.e. how much better or
 *   worse off the market is once both demand growth (net growth × RTO
 *   peak-attendance factor) and supply levers are applied:
 *   (usableSeatsScenario − demandScenario) − (installedCapacity − demandToday).
 *   This is why a market can show a *positive* delta even while growing:
 *   if its supply-side seat gain (sharing/give-back minus hold/waste)
 *   outweighs its RTO-adjusted demand growth, headroom improves.
 * - "Crowded" flag: assignPctScenario at/above 95% (same RTO-adjusted demand
 *   used for assignment, just a lower early-warning threshold than the 100%
 *   over-capacity line).
 * - Guardrail breaches: density floor (sqft ÷ usableSeats below the floor)
 *   and target buffer (scenario headroom % below the buffer) are flagged
 *   independently of the over-capacity state.
 *
 * WHATIF-CAPACITY-PHASING-S1: the capacity ceiling is now horizon-aware.
 * When `market.locations` is present (the per-office array the Plan matrix
 * already carries), "Today" capacity is the sum of computeOfficeHorizonCapacity()
 * evaluated at now, and "Scenario" capacity is the same sum evaluated at
 * (now + levers.horizonMonths) — so a build office's plannedSeats phase in
 * once its onlineDate is reached, and an exiting office's installedCapacity
 * phases out once its closeDate is reached, exactly matching the Timeline's
 * lifecycle logic. When `market.locations` is absent, both figures fall back
 * to the static `market.installedCapacity`/`totalSeats` total (unchanged
 * legacy behavior for callers that only have market-level rollups).
 *
 * @param {{ headcount: number, installedCapacity: number, sqft: number, locations?: object[] }} market
 * @param {object} levers  Merged WHATIF_DEFAULT_LEVERS-shaped lever state
 * @param {{ densityFloorSfPerSeat: number, targetBufferPct: number }} [guardrails]
 * @returns {{
 *   assignPctToday: number, assignPctScenario: number,
 *   usableSeatsToday: number, usableSeatsScenario: number,
 *   demandToday: number, demandScenario: number,
 *   deltaSeats: number, overCapacity: boolean, crowded: boolean,
 *   densityBreach: boolean, bufferBreach: boolean,
 * }}
 */
export function computeWhatIfMarketScenario(market, levers, guardrails = WHATIF_GUARDRAILS) {
  const staticInstalledCapacity = market.installedCapacity ?? market.totalSeats ?? 0;
  const headcount               = market.headcount ?? market.totalHeadcount ?? 0;
  const sqft                    = market.sqft ?? market.totalSqFt ?? 0;

  // Horizon-aware capacity: "Today" is evaluated at now, "Scenario" at
  // now + horizonMonths, both via the same per-office lifecycle helper used
  // by the Timeline pane. Falls back to the static market-level total when
  // no per-location array is available (e.g. market-only rollup callers).
  const hasLocations = Array.isArray(market.locations);
  const now          = new Date();
  const horizonDate   = addMonthsUtc(now, levers.horizonMonths ?? 0);
  const installedCapacityToday    = hasLocations
    ? deriveMarketCapacityStatsAtHorizon(market.locations, now).capacity
    : staticInstalledCapacity;
  const installedCapacityScenario = hasLocations
    ? deriveMarketCapacityStatsAtHorizon(market.locations, horizonDate).capacity
    : staticInstalledCapacity;

  const demandToday    = headcount;
  const usableSeatsToday = installedCapacityToday;
  const assignPctToday = usableSeatsToday > 0
    ? (demandToday / usableSeatsToday) * 100
    : (demandToday > 0 ? Infinity : 0);

  // RTO peak-attendance factor, normalized against the default RTO lever so
  // it is a no-op (1.0) unless the planner actually moves the RTO slider.
  const baselinePeak        = peakRatio(WHATIF_DEFAULT_LEVERS.rtoDaysPerMonth);
  const scenarioPeak        = peakRatio(levers.rtoDaysPerMonth);
  const rtoFactor           = scenarioPeak / baselinePeak;

  const effectiveNetGrowthPct = getEffectiveNetGrowthRate(market, levers);
  const grownDemand         = computeWhatIfDemand(headcount, effectiveNetGrowthPct, levers.horizonMonths);
  const demandScenario      = grownDemand * rtoFactor;
  const usableSeatsScenario = computeUsableSeats(installedCapacityScenario, levers);
  const assignPctScenario   = usableSeatsScenario > 0
    ? (demandScenario / usableSeatsScenario) * 100
    : (demandScenario > 0 ? Infinity : 0);

  const headroomToday    = usableSeatsToday - demandToday;
  const headroomScenario = usableSeatsScenario - demandScenario;
  // Seats-needed delta, NOT headroom delta: headroom moves opposite to demand
  // (more seats needed -> less headroom), so a naive
  // headroomScenario - headroomToday flips the sign the UI wants. The bar/
  // delta text describes "how many more seats does the scenario need than
  // today", which is demandScenario - demandToday directly.
  const deltaSeats        = Math.round(demandScenario - demandToday);

  // MARKET-PLAN-HEADROOM-S1: physical max-density ceiling, horizon-aware
  // exactly like installed capacity above. hasHeadroomData is false (and all
  // usableMaxSeats* fall back to the installed-capacity values) when no
  // office in the market has max_capacity on file — no fabricated ceiling.
  const hasHeadroomData = hasLocations
    && market.locations.some((l) => typeof l.maxCapacity === "number");
  const maxDensityHeadroomToday    = hasLocations
    ? deriveMarketHeadroomAtHorizon(market.locations, now)
    : null;
  const maxDensityHeadroomScenario = hasLocations
    ? deriveMarketHeadroomAtHorizon(market.locations, horizonDate)
    : null;
  const maxCapacityToday    = installedCapacityToday    + (maxDensityHeadroomToday    ?? 0);
  const maxCapacityScenario = installedCapacityScenario + (maxDensityHeadroomScenario ?? 0);
  // Apply the same supply levers to the max-density ceiling as to installed
  // capacity so the two scales stay comparable; "today" never applies levers
  // (matches usableSeatsToday === installedCapacityToday above).
  const usableMaxSeatsToday    = hasHeadroomData ? (usableSeatsToday + (maxDensityHeadroomToday ?? 0)) : usableSeatsToday;
  const usableMaxSeatsScenario = hasHeadroomData ? computeUsableSeats(maxCapacityScenario, levers) : usableSeatsScenario;

  const buildEvents       = hasLocations ? collectBuildEvents(market.locations, now, horizonDate) : [];
  const futureBuildEvents = hasLocations ? collectBuildEvents(market.locations, horizonDate, FAR_FUTURE_DATE) : [];
  const exitEvents        = hasLocations ? collectExitEvents(market.locations, now, horizonDate) : [];
  const futureExitEvents  = hasLocations ? collectExitEvents(market.locations, horizonDate, FAR_FUTURE_DATE) : [];

  const crowded         = assignPctScenario >= 95;
  // Over-capacity now only fires past the physical max-density ceiling when
  // that data is on file; a scenario between installed and max density is
  // "Densify" instead (see WhatIfPane). Markets with no max_capacity data
  // keep the original installed-capacity threshold — unchanged behavior.
  const overCapacity    = hasHeadroomData
    ? demandScenario > usableMaxSeatsScenario
    : assignPctScenario > 100;
  const densify         = hasHeadroomData
    && assignPctScenario > 100
    && demandScenario <= usableMaxSeatsScenario;

  const sfPerSeatScenario = usableSeatsScenario > 0 ? sqft / usableSeatsScenario : Infinity;
  const densityBreach     = sfPerSeatScenario < guardrails.densityFloorSfPerSeat;
  const bufferPctScenario = usableSeatsScenario > 0 ? (headroomScenario / usableSeatsScenario) * 100 : -Infinity;
  const bufferBreach      = bufferPctScenario < guardrails.targetBufferPct;

  return {
    assignPctToday, assignPctScenario,
    usableSeatsToday, usableSeatsScenario,
    demandToday, demandScenario,
    deltaSeats, overCapacity, crowded,
    densityBreach, bufferBreach,
    hasHeadroomData,
    usableMaxSeatsToday, usableMaxSeatsScenario,
    densify,
    buildEvents,
    futureBuildEvents,
    exitEvents,
    futureExitEvents,
    capacityToday:    installedCapacityToday,
    capacityScenario: installedCapacityScenario,
  };
}

/**
 * Portfolio-level roll-up across all markets' What-If scenarios, for the
 * summary banner above the market list.
 *
 * @param {Array<{ market: object, scenario: ReturnType<typeof computeWhatIfMarketScenario> }>} rows
 * @returns {{ overCapacityScenarioCount: number, overCapacityTodayCount: number, netDeltaSeats: number, anyCrowded: boolean }}
 */
export function summarizeWhatIfPortfolio(rows) {
  let overCapacityScenarioCount = 0;
  let overCapacityTodayCount    = 0;
  let netDeltaSeats             = 0;
  let anyCrowded                = false;

  for (const { scenario } of rows) {
    if (scenario.overCapacity) overCapacityScenarioCount += 1;
    if (scenario.assignPctToday > 100) overCapacityTodayCount += 1;
    netDeltaSeats += scenario.deltaSeats;
    if (scenario.crowded) anyCrowded = true;
  }

  return { overCapacityScenarioCount, overCapacityTodayCount, netDeltaSeats, anyCrowded };
}

// ─── Shared portfolio KPI tiles (PLAN-WHATIF-REDESIGN-S1) ────────────────────
// Both Plan mode (Today-only) and What-If mode (Today vs Scenario) render the
// same 5-tile set: Markets, Seats needed, Assignment ratio, Markets over
// capacity, Portfolio balance. The two aggregate functions below compute the
// underlying numbers from the same per-market fields the rest of the Plan
// page already uses (installedCapacity/totalSeats, headcount/totalHeadcount),
// so no new /api/plan fields are required.

/**
 * Today snapshot for a single market — same "today" fields
 * computeWhatIfMarketScenario derives, exposed standalone so Plan mode's KPI
 * strip (which has no lever/scenario concept) can share the exact same
 * capacity/headcount reads.
 *
 * @param {object} market
 * @returns {{ installedCapacity: number, headcount: number, assignPct: number }}
 */
export function computeMarketTodaySnapshot(market) {
  // CAPACITY-DEFINITION-FIX: market.totalSeats is already restricted to usable
  // stages (operational + dormant) by the server-side GET /api/plan handler using
  // PLAN_USABLE_CAPACITY_STAGES. No client-side re-filtering of locations is
  // required here — the server is the single filter gate.
  const installedCapacity = market.installedCapacity ?? market.totalSeats ?? 0;
  const headcount         = market.headcount ?? market.totalHeadcount ?? 0;
  const assignPct = installedCapacity > 0
    ? (headcount / installedCapacity) * 100
    : (headcount > 0 ? Infinity : 0);
  return { installedCapacity, headcount, assignPct };
}

/**
 * Compute rent-only and opex-only portfolio totals from the same active-market
 * location set used by computePortfolioKpis. Derived from per-location fields
 * already present in the /api/plan payload:
 *   annualRentUsd — rent-only cost (excludes opex)
 *   annualCost    — fully-loaded cost (rent + opex)
 *   opex          = annualCost − (annualRentUsd ?? 0)
 *
 * Uses non-closed locations only (stage !== "closed"), mirroring the
 * server-side totalAnnualCost computation in plan.js.
 * Returns null for a component when no location in the portfolio carries that
 * component — never substitutes 0 for missing data.
 *
 * @param {object[]} markets  canonical active markets (same array as passed to computePortfolioKpis)
 * @returns {{ rentTotal: number|null, opexTotal: number|null }}
 */
export function computeAnnualCostBreakdown(markets) {
  let rentTotal = null;
  let opexTotal = null;

  for (const market of (markets ?? [])) {
    for (const loc of (market.locations ?? [])) {
      if (["draft", "closed"].includes(loc.stage ?? "")) continue;

      if (loc.annualRentUsd != null) {
        rentTotal = (rentTotal ?? 0) + loc.annualRentUsd;
      }
      // opex = fully-loaded cost minus rent component (0 when rent is unknown)
      if (loc.annualCost != null) {
        const opex = loc.annualCost - (loc.annualRentUsd ?? 0);
        opexTotal = (opexTotal ?? 0) + opex;
      }
    }
  }

  return { rentTotal, opexTotal };
}

/**
 * Plan-mode (Today-only) portfolio KPI aggregates.
 *
 * MARKET-PLAN-LEADERSHIP-VIEW-S1: adds totalAnnualCost — the sum of every
 * active market's totalAnnualCost (already computed server-side in
 * GET /api/plan, USD-normalized). Markets with no cost data on file
 * (totalAnnualCost == null) are excluded from the sum rather than treated
 * as 0, and annualCostPartial is set so the tile can flag an incomplete
 * total instead of silently under-reporting it.
 *
 * @param {object[]} allMarkets
 * @returns {{ marketsCount: number, seatsNeeded: number, peakUtilPct: number, overCapacityCount: number, balance: number, totalAnnualCost: number|null, annualCostPartial: boolean }}
 */
export function computePortfolioKpis(allMarkets) {
  let seatsNeeded       = 0;
  let capacity          = 0;
  let overCapacityCount = 0;
  let totalAnnualCost   = null;
  let hasAnyCost        = false;
  let missingCostCount  = 0;

  for (const market of (allMarkets ?? [])) {
    const { installedCapacity, headcount, assignPct } = computeMarketTodaySnapshot(market);
    seatsNeeded += headcount;
    capacity    += installedCapacity;
    if (assignPct > 100) overCapacityCount += 1;

    const cost = market.totalAnnualCost;
    if (typeof cost === "number" && !isNaN(cost)) {
      hasAnyCost      = true;
      totalAnnualCost = (totalAnnualCost ?? 0) + cost;
    } else {
      missingCostCount += 1;
    }
  }

  const peakUtilPct = capacity > 0 ? (seatsNeeded / capacity) * 100 : 0;
  const balance     = capacity - seatsNeeded;

  return {
    marketsCount: (allMarkets ?? []).length,
    seatsNeeded,
    peakUtilPct,
    overCapacityCount,
    balance,
    totalAnnualCost: hasAnyCost ? totalAnnualCost : null,
    annualCostPartial: hasAnyCost && missingCostCount > 0,
  };
}

// ─── MARKET-PLAN-LEADERSHIP-VIEW-S1 — "Next decision" + decisions-ahead ─────
//
// Reuses the exact same decision-point vocabulary as the Leases Action view
// (ACTION_DATE_TYPES, from deriveLeaseActions.js) rather than re-deriving a
// parallel list, so "what counts as a decision" never diverges between the
// two surfaces. A market's next decision is the soonest, still-future
// decision-point date across its non-closed locations:
//   1. A location's nextDeadline whose critical-date `type` is in
//      ACTION_DATE_TYPES (renewal_notice, renewal_option, break_option,
//      notice_deadline, rolling_review) — the same governing-date concept
//      leases use.
//   2. Falling back to the location's plain leaseExpiry when no such
//      critical date is on file (never fabricated — only used when a real
//      expiry date exists and isn't a sentinel/far-future placeholder).
// Returns null when no location has any qualifying date — the table/strip
// render "—" rather than a fabricated decision.

const SENTINEL_YEAR_CUTOFF = 2100;

function isSentinelDateValue(raw) {
  if (!raw) return true;
  const s = typeof raw === "string" ? raw : String(raw);
  if (s.startsWith("9999-")) return true;
  const yr = parseInt(s.slice(0, 4), 10);
  return isNaN(yr) || yr > SENTINEL_YEAR_CUTOFF;
}

/**
 * @param {object} market  Market row with a `locations` array (camelCase;
 *                          each location has stage, nextDeadline, leaseExpiry).
 * @param {Date} [today]
 * @returns {{ date: string, label: string, days: number, isNear: boolean } | null}
 *          isNear === true when the decision falls within the next 6 months.
 */
export function deriveMarketNextDecision(market, today = new Date()) {
  const locations = Array.isArray(market?.locations) ? market.locations : [];
  let best = null;

  for (const loc of locations) {
    if (INACTIVE_STAGES.has(loc.stage ?? "")) continue;

    let candidateDate = null;
    let candidateLabel = null;

    const deadline = loc.nextDeadline;
    if (deadline?.dueDate && !isSentinelDateValue(deadline.dueDate) && ACTION_DATE_TYPES.has(deadline.type)) {
      candidateDate  = deadline.dueDate;
      candidateLabel = deadline.label ?? "Decision due";
    } else if (loc.leaseExpiry && !isSentinelDateValue(loc.leaseExpiry)) {
      candidateDate  = loc.leaseExpiry;
      candidateLabel = "Lease expiration";
    }

    if (!candidateDate) continue;
    const d = parseTimelineDate(candidateDate);
    if (!d || d <= today) continue;

    if (!best || d < best.dateObj) {
      best = { dateObj: d, date: candidateDate, label: candidateLabel };
    }
  }

  if (!best) return null;

  const days = Math.round((best.dateObj - today) / (1000 * 60 * 60 * 24));
  const isNear = days <= 182; // ~6 months

  return { date: best.date, label: best.label, days, isNear };
}

/**
 * Portfolio-wide "decisions ahead" rollup: active markets whose next
 * decision (see deriveMarketNextDecision) falls within `horizonMonths`.
 * Powers the strip above the Plan table: "N markets need a decision in the
 * next 6 months — $X/yr, Y seats at stake", plus the top-$ market chips.
 *
 * Dollars-at-stake and seats-at-stake are summed only from markets with
 * known values (totalAnnualCost/totalSeats) — a market with no cost data on
 * file contributes 0 to the dollar total but is still counted toward `count`
 * and flagged via `dollarsPartial`, so the total is never silently inflated
 * or fabricated.
 *
 * @param {object[]} markets
 * @param {Date} [today]
 * @param {number} [horizonMonths=6]
 * @returns {{
 *   count: number,
 *   totalDollars: number,
 *   dollarsPartial: boolean,
 *   totalSeats: number,
 *   topMarkets: Array<{ id: string, name: string, annualCost: number|null, days: number }>,
 * } | null}  null when no market has a decision within the horizon.
 */
export function selectDecisionsAhead(markets, today = new Date(), horizonMonths = 6) {
  const horizonDate = addMonthsUtc(today, horizonMonths);
  const candidates = [];

  for (const market of (markets ?? [])) {
    if (!isWhatIfActiveMarket(market)) continue;
    const decision = deriveMarketNextDecision(market, today);
    if (!decision) continue;
    const decisionDate = parseTimelineDate(decision.date);
    if (!decisionDate || decisionDate > horizonDate) continue;
    candidates.push({ market, decision });
  }

  if (candidates.length === 0) return null;

  let totalDollars   = 0;
  let dollarsPartial = false;
  let totalSeats     = 0;

  for (const { market } of candidates) {
    const cost = market.totalAnnualCost;
    if (typeof cost === "number" && !isNaN(cost)) {
      totalDollars += cost;
    } else {
      dollarsPartial = true;
    }
    const seats = market.totalSeats ?? market.installedCapacity;
    if (typeof seats === "number" && !isNaN(seats)) totalSeats += seats;
  }

  const topMarkets = candidates
    .slice()
    .sort((a, b) => {
      const ca = typeof a.market.totalAnnualCost === "number" ? a.market.totalAnnualCost : -1;
      const cb = typeof b.market.totalAnnualCost === "number" ? b.market.totalAnnualCost : -1;
      return cb - ca;
    })
    .slice(0, 3)
    .map(({ market, decision }) => ({
      id:         market.id,
      name:       market.name,
      annualCost: typeof market.totalAnnualCost === "number" ? market.totalAnnualCost : null,
      days:       decision.days,
    }));

  // All candidates sorted soonest-first — used by the decision-band popover
  // and "View all" filter in StatusBand / usePlanStatusMessages (CONTEXT-BANDS-S2).
  const allMarkets = candidates
    .slice()
    .sort((a, b) => a.decision.days - b.decision.days)
    .map(({ market, decision }) => ({
      id:               market.id,
      name:             market.name,
      annualCost:       typeof market.totalAnnualCost === "number" ? market.totalAnnualCost : null,
      days:             decision.days,
      nextDecisionDate: decision.date,
    }));

  return {
    count: candidates.length,
    totalDollars,
    dollarsPartial,
    totalSeats,
    topMarkets,
    markets: allMarkets,
  };
}

/**
 * Format a QoQ delta between a current KPI value and its prior-period
 * (snapshot) value. Returns null when either value is unavailable — callers
 * must render nothing (not a fabricated "no change") in that case, matching
 * the requirement that deltas only ever appear when real history exists.
 *
 * @param {number|null|undefined} current
 * @param {number|null|undefined} previous
 * @param {{ unit?: "count"|"currency"|"percent", decimals?: number }} [opts]
 * @returns {{ direction: "up"|"down"|"flat", text: string } | null}
 */
export function formatKpiQoQDelta(current, previous, opts = {}) {
  if (typeof current !== "number" || isNaN(current)) return null;
  if (typeof previous !== "number" || isNaN(previous)) return null;

  const { unit = "count", decimals = 0 } = opts;
  const diff = current - previous;
  const direction = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
  const arrow = direction === "up" ? "▲" : direction === "down" ? "▼" : "▬";
  const abs = Math.abs(diff);

  let formatted;
  if (unit === "currency") {
    formatted = `$${Math.round(abs).toLocaleString()}`;
  } else if (unit === "percent") {
    formatted = `${abs.toFixed(decimals)}pp`;
  } else {
    formatted = abs.toLocaleString();
  }

  return { direction, text: `${arrow} ${formatted} QoQ` };
}

/**
 * What-If mode Today-vs-Scenario portfolio KPI aggregates, built from the
 * same {market, scenario} rows WhatIfPane already computes via
 * computeWhatIfMarketScenario.
 *
 * @param {Array<{ market: object, scenario: ReturnType<typeof computeWhatIfMarketScenario> }>} rows
 * @returns {{
 *   marketsCount: number,
 *   seatsNeededToday: number, seatsNeededScenario: number,
 *   peakUtilToday: number, peakUtilScenario: number,
 *   overCapacityToday: number, overCapacityScenario: number,
 *   balanceToday: number, balanceScenario: number,
 * }}
 */
export function computeWhatIfPortfolioKpis(rows) {
  let seatsNeededToday    = 0;
  let seatsNeededScenario = 0;
  let capacityToday       = 0;
  let capacityScenario    = 0;
  let overCapacityToday   = 0;
  let overCapacityScenario = 0;

  for (const { scenario } of (rows ?? [])) {
    seatsNeededToday    += scenario.demandToday;
    seatsNeededScenario += scenario.demandScenario;
    capacityToday       += scenario.usableSeatsToday;
    capacityScenario    += scenario.usableSeatsScenario;
    if (scenario.assignPctToday > 100) overCapacityToday += 1;
    if (scenario.overCapacity)         overCapacityScenario += 1;
  }

  const peakUtilToday    = capacityToday    > 0 ? (seatsNeededToday    / capacityToday)    * 100 : 0;
  const peakUtilScenario = capacityScenario > 0 ? (seatsNeededScenario / capacityScenario) * 100 : 0;

  return {
    marketsCount: (rows ?? []).length,
    seatsNeededToday, seatsNeededScenario,
    peakUtilToday, peakUtilScenario,
    overCapacityToday, overCapacityScenario,
    balanceToday:    capacityToday    - seatsNeededToday,
    balanceScenario: capacityScenario - seatsNeededScenario,
  };
}

// ─── What-If open-role allocation math (WHATIF-S2) ───────────────────────────

/**
 * Fractionally allocate open Greenhouse roles to markets.
 *
 * Each posting that lists N non-remote offices contributes 1/N to each of
 * those offices' markets (expected), 0 to all (low), and 1 to all (high).
 * Roles with zero non-remote offices after filtering are skipped entirely.
 * REMOTE postings (isRemote=true) are excluded from allocation but counted
 * in the returned roles array.
 *
 * Market matching: case-insensitive substring in either direction
 * (e.g. "San Francisco, CA" matches market "San Francisco").
 *
 * @param {Array<{ id: number|string, title: string, offices: {name: string}[], isRemote: boolean }>} roles
 * @param {string[]} marketNames  Canonical market name strings
 * @returns {{ alloc: Record<string, {expected: number, low: number, high: number}>, unmappedCount: number }}
 */
export function allocateRolesToMarkets(roles, marketNames) {
  // Initialise alloc map for every known market
  const alloc = {};
  for (const m of marketNames) {
    alloc[m] = { expected: 0, low: 0, high: 0 };
  }

  let unmappedCount = 0;

  for (const role of (roles ?? [])) {
    // Fully-remote roles contribute nothing
    if (role.isRemote) continue;

    const offices = (role.offices ?? []).filter(
      (o) => !o.name.toLowerCase().includes("remote")
    );

    if (offices.length === 0) continue;

    // Match each office to known markets (one office may match multiple markets,
    // though in practice it matches at most one).
    const matchedMarkets = new Set();
    for (const office of offices) {
      for (const m of marketNames) {
        if (_officeMatchesMarket(office.name, m)) {
          matchedMarkets.add(m);
        }
      }
    }

    if (matchedMarkets.size === 0) {
      unmappedCount++;
      continue;
    }

    const n = matchedMarkets.size;
    for (const m of matchedMarkets) {
      alloc[m].expected += 1 / n;
      // low = 0 (role may go entirely elsewhere — already initialised to 0)
      alloc[m].high += 1;
    }
  }

  return { alloc, unmappedCount };
}

/**
 * Case-insensitive bidirectional substring match between office and market names.
 * @param {string} officeName
 * @param {string} marketName
 * @returns {boolean}
 */
function _officeMatchesMarket(officeName, marketName) {
  const o = (officeName ?? "").toLowerCase();
  const m = (marketName ?? "").toLowerCase();
  return o.includes(m) || m.includes(o);
}

// ─── PLAN-TIMELINE-COVERAGE-S1 — Timeline (coverage Gantt) helpers ─────────
//
// Pure, DOM-free date-interval math for the Plan page's read-only Timeline
// view. All functions accept/return Date objects (UTC, midnight) so callers
// (TimelinePane.jsx) can do straightforward ms-based position math without
// re-deriving stage semantics.
//
// Missing-date handling (no fabricated dates): when an office lacks
// online_date while still in a build-ish stage, or lacks close_date while
// exiting, the returned segment is marked open:true + a flag string instead
// of guessing a date. The caller renders that as a dashed/open-ended bar
// plus a "date not set" indicator.

const IN_BUILD_STAGES = new Set(["build"]);

// Sentinel far-future date used to collect builds whose onlineDate falls
// beyond the current horizon — callers should never rely on the absolute
// value of this constant, only use it as an upper-bound argument.
const FAR_FUTURE_DATE = new Date("2999-01-01T00:00:00.000Z");

/**
 * WHATIF-BUILD-ONLINE-INDICATOR-S1 — collect build events that phase in
 * within a given date window (todayDate, horizonDate] from the set of
 * IN_BUILD_STAGES offices in `locations`.
 *
 * Each returned event has:
 *   officeId      — location id
 *   officeName    — display name (location_alias or id fallback)
 *   seatsAdded    — positive capacity delta within this requested window
 *   onlineDate    — Date object (UTC midnight)
 *   capacityBefore — cumulative installed capacity just before this event
 *   capacityAfter  — cumulative installed capacity just after this event
 *
 * Events are ordered ascending by onlineDate so capacityBefore/After chain
 * correctly when multiple builds phase in.
 *
 * @param {object[]} locations  Location rows for a market.
 * @param {Date} todayDate      Lower bound (exclusive).
 * @param {Date} horizonDate    Upper bound (inclusive).
 * @returns {Array<{officeId:string, officeName:string, seatsAdded:number, onlineDate:Date, capacityBefore:number, capacityAfter:number}>}
 */
export function collectBuildEvents(locations, todayDate, horizonDate) {
  const today   = toUtcDate(todayDate);
  const horizon = toUtcDate(horizonDate);

  const candidates = [];
  for (const loc of (locations ?? [])) {
    const stage = loc?.stage ?? "";
    if (!IN_BUILD_STAGES.has(stage)) continue;
    const capacityAtToday = computeOfficeHorizonCapacity(loc, today);
    const capacityAtHorizon = computeOfficeHorizonCapacity(loc, horizon);
    const seatsAdded = capacityAtHorizon - capacityAtToday;
    if (seatsAdded <= 0) continue;

    const onlineDate = parseTimelineDate(loc?.onlineDate);
    const scheduleDates = (Array.isArray(loc?.futureScheduleRows) ? loc.futureScheduleRows : [])
      .map((row) => parseTimelineDate(row?.effectiveDate))
      .filter((date) => date != null && date > today && date <= horizon)
      .sort((a, b) => a - b);
    const eligibleDates = [
      ...(onlineDate != null && onlineDate > today && onlineDate <= horizon ? [onlineDate] : []),
      ...scheduleDates,
    ].sort((a, b) => a - b);
    if (eligibleDates.length === 0) continue;
    // A build can be sourced by either lifecycle metadata or a future
    // capacity row. Find the first date that actually raises capacity above
    // the lower-bound value; this keeps phased builds in one window and
    // prevents them from appearing in both adjacent What-If windows.
    const eventDate = eligibleDates.find(
      (date) => computeOfficeHorizonCapacity(loc, date) > capacityAtToday,
    );
    if (eventDate != null) candidates.push({ loc, onlineDate: eventDate, seatsAdded });
  }
  candidates.sort((a, b) => a.onlineDate - b.onlineDate);

  const capacityAtToday = (locations ?? []).reduce((sum, loc) => {
    return sum + computeOfficeHorizonCapacity(loc, today);
  }, 0);

  const events = [];
  let runningCapacity = capacityAtToday;
  for (const { loc, onlineDate, seatsAdded } of candidates) {
    const capacityBefore = runningCapacity;
    const capacityAfter  = runningCapacity + seatsAdded;
    events.push({
      // SPACE-PIPELINE-NET-S1: accept both the getPlan() camelCase shape
      // (locationId/locationAlias) and the legacy id/location_alias shape
      // already exercised by this module's own unit tests — some callers
      // (e.g. WhatIfPane fixtures) still use the legacy names.
      officeId:       loc.locationId ?? loc.id,
      officeName:     loc.locationAlias ?? loc.location_alias ?? loc.name ?? loc.locationId ?? loc.id,
      seatsAdded,
      onlineDate,
      capacityBefore,
      capacityAfter,
    });
    runningCapacity = capacityAfter;
  }
  return events;
}

// SPACE-PIPELINE-NET-CONSOLIDATION-S1: an office can shed seats within a
// window without ever having disposition="exit" set — a scheduled
// installed-seat drop on file (location_capacity_schedule, surfaced as
// loc.futureScheduleRows) is just as real a departure for netting/breakdown
// purposes. This finds the latest schedule row whose effectiveDate falls
// inside (today, horizon] — i.e. the row that is actually responsible for a
// horizon-effective capacity decrease — or null when no such row exists or
// the office's capacity does not decrease across the window.
function findScheduleCapacityDropDate(loc, today, horizon) {
  const rows = loc?.futureScheduleRows;
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const capacityToday   = computeOfficeHorizonCapacity(loc, today);
  const capacityHorizon = computeOfficeHorizonCapacity(loc, horizon);
  if (capacityHorizon >= capacityToday) return null;

  let dropDate = null;
  for (const r of rows) {
    const d = parseTimelineDate(r?.effectiveDate);
    if (d == null || d <= today || d > horizon) continue;
    if (dropDate == null || d > dropDate) dropDate = d;
  }
  return dropDate;
}

/**
 * WHATIF-EXIT-SEGMENT-S1 / SPACE-PIPELINE-NET-CONSOLIDATION-S1 — collect
 * exit events for a market: operational offices with disposition=exit whose
 * closeDate falls within (todayDate, horizonDate], AND offices with no exit
 * disposition on file whose scheduled installed-seat capacity (
 * loc.futureScheduleRows) still drops within the same window (e.g. a planned
 * downsize/closure that hasn't been flagged as an exit disposition yet).
 * Both cases represent seats actually leaving the market over the window and
 * must be named in the breakdown, not silently absorbed into the net-only
 * figure.
 *
 * Each returned event has:
 *   officeId       — location id
 *   officeName     — display name (location_alias or id fallback)
 *   seatsRemoved   — horizon-effective capacity that phases out by closeDate
 *   closeDate      — Date object (UTC midnight) — the disposition closeDate,
 *                    or the schedule row's effectiveDate for schedule-driven
 *                    drops
 *   capacityBefore — cumulative capacity just before this exit
 *   capacityAfter  — cumulative capacity just after this exit
 *
 * Events are ordered ascending by closeDate so capacityBefore/After chain
 * correctly when multiple offices exit within the horizon.
 *
 * @param {object[]} locations  Location rows for a market.
 * @param {Date} todayDate      Lower bound (exclusive).
 * @param {Date} horizonDate    Upper bound (inclusive).
 * @returns {Array<{officeId:string, officeName:string, seatsRemoved:number, closeDate:Date, capacityBefore:number, capacityAfter:number}>}
 */
export function collectExitEvents(locations, todayDate, horizonDate) {
  const today   = toUtcDate(todayDate);
  const horizon = toUtcDate(horizonDate);

  const candidates = [];
  for (const loc of (locations ?? [])) {
    const stage = loc?.stage ?? "";
    if (INACTIVE_STAGES.has(stage)) continue;
    if (IN_BUILD_STAGES.has(stage)) continue;

    if (loc?.disposition === EXIT_DISPOSITION) {
      const closeDate = parseTimelineDate(loc?.closeDate);
      if (closeDate == null) continue;
      if (closeDate <= today || closeDate > horizon) continue;
      candidates.push({ loc, closeDate });
      continue;
    }

    const scheduleDropDate = findScheduleCapacityDropDate(loc, today, horizon);
    if (scheduleDropDate != null) {
      candidates.push({ loc, closeDate: scheduleDropDate });
    }
  }
  candidates.sort((a, b) => a.closeDate - b.closeDate);

  const capacityAtToday = (locations ?? []).reduce((sum, loc) => {
    return sum + computeOfficeHorizonCapacity(loc, today);
  }, 0);

  const events = [];
  let runningCapacity = capacityAtToday;
  for (const { loc, closeDate } of candidates) {
    // Uniform for both disposition-based and schedule-driven exits: the
    // office's own horizon-effective capacity today vs. at its closeDate.
    // For a disposition=exit office with no schedule data this reduces to
    // the pre-existing flat installedCapacity (capacity at closeDate is 0),
    // matching prior behavior exactly.
    const seatsRemoved = Math.max(
      0,
      computeOfficeHorizonCapacity(loc, today) - computeOfficeHorizonCapacity(loc, closeDate)
    );
    const capacityBefore = runningCapacity;
    const capacityAfter  = runningCapacity - seatsRemoved;
    events.push({
      // SPACE-PIPELINE-NET-S1: see matching comment in collectBuildEvents.
      officeId:       loc.locationId ?? loc.id,
      officeName:     loc.locationAlias ?? loc.location_alias ?? loc.name ?? loc.locationId ?? loc.id,
      seatsRemoved,
      closeDate,
      capacityBefore,
      capacityAfter,
    });
    runningCapacity = capacityAfter;
  }
  return events;
}

// locations.disposition === 'exit' is the plan-of-record signal that an
// office is scheduled to close — see plan-disposition-vocabulary memory
// note: 'exit' (not 'vacate') is the locations.disposition value.
const EXIT_DISPOSITION = "exit";

/**
 * Parse a DATE column value (may arrive as "YYYY-MM-DD" or a full ISO
 * timestamp from Postgres) into a UTC midnight Date. Returns null for
 * null/undefined/invalid input — callers must treat null as "unknown",
 * never substitute a guessed date.
 * @param {string|null|undefined} v
 * @returns {Date|null}
 */
export function parseTimelineDate(v) {
  if (v == null || v === "") return null;
  const s = String(v).slice(0, 10);
  const d = new Date(`${s}T00:00:00.000Z`);
  return isNaN(d.getTime()) ? null : d;
}

function toUtcDate(v) {
  return v instanceof Date ? v : new Date(v);
}

/**
 * Derive the lifecycle segments (in-build / operational / exiting) for a
 * single office, clipped to [rangeStart, rangeEnd].
 *
 * @param {object} office        Plan location row — needs stage, disposition,
 *                                onlineDate, closeDate (camelCase).
 * @param {Date|string} rangeStart
 * @param {Date|string} rangeEnd
 * @returns {Array<{type: 'in-build'|'operational'|'exiting', start: Date, end: Date, open: boolean, flag: string|null}>}
 */
export function computeOfficeLifecycleSegments(office, rangeStart, rangeEnd) {
  if (office?.stage === "draft") return [];
  const start = toUtcDate(rangeStart);
  const end = toUtcDate(rangeEnd);
  if (!(end > start)) return [];

  const onlineDate = parseTimelineDate(office?.onlineDate);
  const closeDate = parseTimelineDate(office?.closeDate);
  const stage = office?.stage ?? "";
  const isExiting = office?.disposition === EXIT_DISPOSITION;
  const inBuild = IN_BUILD_STAGES.has(stage);
  const segments = [];

  if (inBuild) {
    if (onlineDate == null) {
      // Still building, no known go-live date — open-ended hatched bar
      // spanning the whole visible range, flagged instead of guessed.
      segments.push({ type: "in-build", start, end, open: true, flag: "missing-online-date" });
      return segments;
    }
    if (onlineDate >= end) {
      // Online date is set but falls after the visible window — the whole
      // visible range is still "in build".
      segments.push({ type: "in-build", start, end, open: false, flag: null });
      return segments;
    }
    const buildEnd = onlineDate > start ? onlineDate : start;
    segments.push({ type: "in-build", start, end: buildEnd, open: false, flag: null });
  }

  const liveStart = inBuild
    ? (onlineDate > start ? onlineDate : start)
    : (onlineDate != null && onlineDate > start && onlineDate < end ? onlineDate : start);

  if (!(end > liveStart)) return segments;

  // PLAN-TIMELINE-DATA-WIRING-S1: a non-exiting office that never went
  // through an in-build stage with a recorded online_date has no known real
  // start — rendering a confident full-range fill would silently claim we
  // know when it began operating. Scoped to !isExiting because an exiting
  // office with no closeDate is already flagged open via `openEnd` below
  // (missing-close-date takes priority there — that's the more actionable
  // gap for a location already known to be leaving).
  const unknownStart = !inBuild && !isExiting && onlineDate == null;
  const openEnd = isExiting && closeDate == null;
  const rawEnd = closeDate ?? end;
  const liveEnd = rawEnd < end ? rawEnd : end;
  if (!(liveEnd > liveStart)) return segments;

  segments.push({
    type: isExiting ? "exiting" : "operational",
    start: liveStart,
    end: liveEnd,
    open: unknownStart || openEnd,
    flag: unknownStart ? "missing-online-date" : (openEnd ? "missing-close-date" : null),
  });
  return segments;
}

/**
 * Derive the per-market coverage band for a set of offices over
 * [rangeStart, rangeEnd]: 'gap' where no office is live, 'live' where
 * exactly one is, 'overlap' where more than one is simultaneously live.
 * "Live" = operational or exiting (in-build offices don't count as coverage
 * yet). Adjacent bands with the same status are merged.
 *
 * @param {object[]} offices
 * @param {Date|string} rangeStart
 * @param {Date|string} rangeEnd
 * @returns {Array<{start: Date, end: Date, status: 'gap'|'live'|'overlap'}>}
 */
export function computeMarketCoverageBands(offices, rangeStart, rangeEnd) {
  const start = toUtcDate(rangeStart);
  const end = toUtcDate(rangeEnd);
  if (!(end > start)) return [];

  const intervals = [];
  for (const office of offices ?? []) {
    const segs = computeOfficeLifecycleSegments(office, start, end)
      .filter((s) => s.type !== "in-build");
    for (const s of segs) {
      if (s.end > s.start) intervals.push([s.start.getTime(), s.end.getTime()]);
    }
  }

  if (intervals.length === 0) {
    return [{ start, end, status: "gap" }];
  }

  const points = new Set([start.getTime(), end.getTime()]);
  for (const [s, e] of intervals) { points.add(s); points.add(e); }
  const sorted = Array.from(points).sort((a, b) => a - b);

  const bands = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (b <= a) continue;
    const mid = (a + b) / 2;
    const count = intervals.filter(([s, e]) => s <= mid && e >= mid).length;
    const status = count === 0 ? "gap" : count === 1 ? "live" : "overlap";
    const last = bands[bands.length - 1];
    if (last && last.status === status) {
      last.end = new Date(b);
    } else {
      bands.push({ start: new Date(a), end: new Date(b), status });
    }
  }
  return bands;
}

// ─── PLAN-TIMELINE-HEATMAP-S1 — market × quarter coverage & pressure heatmap ─
//
// Quarter-bucketed variant of the coverage/capacity math above. The Timeline
// view's new default (Direction A) is one row per market and one cell per
// quarter, colored by worst-wins priority: coverage gap > over capacity >
// tight/transition > covered & OK. Deliberately reuses
// computeOfficeHorizonCapacity / deriveMarketCapacityStatsAtHorizon (the
// same capacity-phasing math the What-If lens already uses) rather than
// re-deriving live/gap logic — see those functions' header comments for the
// missing-date philosophy (never fabricate a transition date).

/** Ordered worst-to-best states for the heatmap; kept for readability at
 * call sites, not used for numeric comparisons (each state is decided by
 * explicit early-return priority in computeMarketQuarterState). */
export const QUARTER_STATE_ORDER = ["gap", "over", "tight", "ok"];

/**
 * Start-of-quarter (UTC midnight, day 1 of the quarter's first month) for a
 * given date.
 * @param {Date|string} date
 * @returns {Date}
 */
export function startOfQuarterUTC(date) {
  const d = toUtcDate(date);
  const qStartMonth = Math.floor(d.getUTCMonth() / 3) * 3;
  return new Date(Date.UTC(d.getUTCFullYear(), qStartMonth, 1));
}

/**
 * Add N quarters (3-month blocks) to a date, in UTC.
 * @param {Date|string} date
 * @param {number} n
 * @returns {Date}
 */
export function addQuartersUTC(date, n) {
  const d = toUtcDate(date);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n * 3, 1));
}

/**
 * Build the quarter column headers for the heatmap, starting at the quarter
 * containing `today` and extending forward `quarterCount` quarters (so the
 * first column is always "today's" quarter — the one visually highlighted).
 *
 * @param {number} quarterCount   Number of quarter columns (e.g. 4/10/20 for 12mo/30mo/5yr)
 * @param {Date|string} [today]   Defaults to now; pass explicitly for deterministic tests.
 * @returns {Array<{start: Date, end: Date, year: number, quarter: number, label: string, isToday: boolean}>}
 */
export function buildQuarterColumns(quarterCount, today = new Date()) {
  const anchor = startOfQuarterUTC(today);
  const columns = [];
  for (let i = 0; i < quarterCount; i++) {
    const start = addQuartersUTC(anchor, i);
    const end = addQuartersUTC(anchor, i + 1);
    const quarter = Math.floor(start.getUTCMonth() / 3) + 1;
    columns.push({
      start,
      end,
      year: start.getUTCFullYear(),
      quarter,
      label: `Q${quarter} ${start.getUTCFullYear()}`,
      isToday: i === 0,
    });
  }
  return columns;
}

/**
 * Determine one market's coverage/pressure state for a single quarter
 * interval [quarterStart, quarterEnd), using worst-wins priority:
 * gap > over > tight > ok.
 *
 * - gap: zero live offices this quarter. Live = operational (regardless of
 *   whether its onlineDate is known — an operational office is live by
 *   definition, so missing dates on an already-running office never read as
 *   a gap), or in-build with a known onlineDate <= quarterStart; minus any
 *   office whose closeDate <= quarterStart (already exited by quarter
 *   start).
 * - over: seats-needed (plan baseline headcount, held flat — NOT a What-If
 *   scenario projection) > phased capacity that quarter (via
 *   deriveMarketCapacityStatsAtHorizon, evaluated at quarterStart).
 * - tight: within capacity but past the target buffer, OR an office's
 *   onlineDate/closeDate falls inside this quarter (a transition is
 *   happening even if the math nets out fine).
 * - ok: >=1 live office, within capacity and buffer, no transition this
 *   quarter.
 *
 * @param {{ locations?: object[], headcount?: number, totalHeadcount?: number }} market
 * @param {Date} quarterStart
 * @param {Date} quarterEnd
 * @param {{ targetBufferPct: number }} [guardrails]
 * @returns {'gap'|'over'|'tight'|'ok'}
 */
export function computeMarketQuarterState(market, quarterStart, quarterEnd, guardrails = WHATIF_GUARDRAILS) {
  const offices = (market?.locations ?? []).filter(
    (l) => l.stage !== "draft" && l.stage !== "closed" && l.stage !== "vacated"
  );

  let liveCount = 0;
  let transition = false;

  for (const office of offices) {
    const stage = office?.stage ?? "";
    const inBuild = IN_BUILD_STAGES.has(stage);
    const onlineDate = parseTimelineDate(office?.onlineDate);
    const closeDate = parseTimelineDate(office?.closeDate);
    // FORWARD-CAPACITY-CONSUMERS-READ-SCHEDULE: a scheduled build is "live"
    // for this quarter the moment its schedule row takes effect, even when
    // the legacy onlineDate is unset or falls after quarterStart.
    const scheduleRow = inBuild
      ? effectiveScheduleRowAtHorizon(office?.futureScheduleRows, quarterStart)
      : null;

    const isLive = inBuild
      ? (scheduleRow !== null || (onlineDate != null && onlineDate <= quarterStart))
      : true;
    const hasClosedByStart = closeDate != null && closeDate <= quarterStart;
    if (isLive && !hasClosedByStart) liveCount += 1;

    if (onlineDate != null && onlineDate >= quarterStart && onlineDate < quarterEnd) transition = true;
    if (closeDate != null && closeDate >= quarterStart && closeDate < quarterEnd) transition = true;
    if (inBuild) {
      for (const r of (office?.futureScheduleRows ?? [])) {
        const d = parseTimelineDate(r.effectiveDate);
        if (d != null && d >= quarterStart && d < quarterEnd) transition = true;
      }
    }
  }

  if (liveCount === 0) return "gap";

  const seatsNeeded = market?.headcount ?? market?.totalHeadcount ?? 0;
  const capacity = deriveMarketCapacityStatsAtHorizon(offices, quarterStart).capacity;

  if (seatsNeeded > capacity) return "over";

  const bufferPct = capacity > 0 ? ((capacity - seatsNeeded) / capacity) * 100 : 100;
  const tightOnBuffer = bufferPct < (guardrails?.targetBufferPct ?? 0);

  if (tightOnBuffer || transition) return "tight";

  return "ok";
}

/**
 * Full per-quarter state timeline for one market, given a set of quarter
 * columns (from buildQuarterColumns). Each returned entry carries the
 * column metadata plus the computed `state`.
 *
 * @param {object} market
 * @param {Array<{start: Date, end: Date, label: string, isToday: boolean}>} quarterColumns
 * @param {{ targetBufferPct: number }} [guardrails]
 * @returns {Array<{start: Date, end: Date, label: string, isToday: boolean, state: 'gap'|'over'|'tight'|'ok'}>}
 */
export function computeMarketQuarterTimeline(market, quarterColumns, guardrails = WHATIF_GUARDRAILS) {
  return (quarterColumns ?? []).map((col) => ({
    ...col,
    state: computeMarketQuarterState(market, col.start, col.end, guardrails),
  }));
}
